import {
  TRAVELLING_SERVICE_TYPES_IDS,
  SITTER_PROFILE_PREFIX,
  SERVICE_TYPES,
} from '../constants/app';
import { PROFILE_ABOUT_PAGE_URL } from '../constants/pageLayout';
import {
  BADGE_MAP,
  CALENDAR_UPDATE_THRESHOLD,
  COVID_VACCINATION_ID,
  FENCED_YARD_BADGE_ID,
  POLICE_CHECK_ID,
} from '../constants/search';
import { ServicePreferencesStatus } from '../types/sitter';

import type { Badge } from '../types/search';
import type { AdditionalService, Sitter } from '../types/sitter';
import type { Item as SitterCardData } from '@madpaws/design-system';
import type { SearchData } from '~/services/search/types';

const statesMappings: { [key: string]: string } = {
  'new-south-wales': 'nsw',
  queensland: 'qld',
  'south-australia': 'sa',
  tasmania: 'tas',
  victoria: 'vic',
  'western-australia': 'wa',
};

// NOTE: exported for unit testing purposes only
export const transformStringForUrl = (value: string): string =>
  value.trim().split(/\W+/g).join('-').toLowerCase();

// NOTE: exported for unit testing purposes only
export const getShortStateName = (state: string): string => {
  const stateFullNames = Object.keys(statesMappings);
  const transformedState = transformStringForUrl(state);

  if (stateFullNames.includes(transformedState)) {
    return statesMappings[transformedState];
  }

  return transformedState;
};

export const getSitterProfileUrl = (city: string, rewriteUrl: string, state: string): string => {
  const shortStateName = getShortStateName(state);
  const transformedCity = transformStringForUrl(city);

  return `/${SITTER_PROFILE_PREFIX}/${transformedCity}-${shortStateName}/${rewriteUrl}`;
};

export const isSitterServiceProvidesRwb = (
  serviceTypeId: number | string,
  sitterRWBServices?: Sitter['providesRwbPerService']
): boolean => typeof serviceTypeId === 'number' && !!sitterRWBServices?.includes(serviceTypeId);

export const transformProfileUrl = (city: string, rewriteUrl: string, state: string): string =>
  // If any of the three is empty, return the static alternative link
  city || state || rewriteUrl
    ? getSitterProfileUrl(city, rewriteUrl, state)
    : PROFILE_ABOUT_PAGE_URL;

export const transformSitterServiceStatus = (
  status: number,
  providesRwb: boolean
): ServicePreferencesStatus => {
  if (!status) {
    return ServicePreferencesStatus.INACTIVE;
  }

  if (providesRwb) {
    return ServicePreferencesStatus.REPEAT;
  }

  return ServicePreferencesStatus.ACTIVE;
};

export const getSitterAdditionalServices = (
  sitter: Sitter | null,
  serviceTypeId: number | ''
): AdditionalService[] => {
  if (!sitter) {
    return [];
  }

  const { sitterServices } = sitter;

  const sitterServiceData = sitterServices.data.find(
    (sitterService) => sitterService.serviceTypeId === serviceTypeId
  );

  return sitterServiceData?.additionalServices.data || [];
};

export const getSitterAdditionalTravellingServices = (
  sitter: Sitter | null
): { id: string; totalPrice: number }[] => {
  if (!sitter) {
    return [];
  }

  const { servicesAndRates } = sitter;

  if (!servicesAndRates.data.travelFeeRate.available) {
    return [];
  }

  return [
    {
      id: TRAVELLING_SERVICE_TYPES_IDS.pickup,
      totalPrice: servicesAndRates.data.travelFeeRate.value,
    },
    {
      id: TRAVELLING_SERVICE_TYPES_IDS.dropoff,
      totalPrice: servicesAndRates.data.travelFeeRate.value,
    },
  ];
};

export const transformBadgeData = (
  badgeData: Array<Omit<Badge, 'icon'> & { icon: string }> = [],
  lastCalendarUpdate: number,
  providesRwbPerService: number[],
  repeatGuests: number,
  service?: string
  // _city?: string,
  // _state?: string
): Badge[] => {
  const filteredBadgeData = badgeData
    .filter(({ id }) => Object.keys(BADGE_MAP).includes(id.toString()))
    .map(({ id, name }) => {
      let badgeName = name;

      if (id === FENCED_YARD_BADGE_ID) {
        badgeName = 'Fenced yard';
      }

      if (id === POLICE_CHECK_ID) {
        badgeName = 'Police check';
      }

      if (id === COVID_VACCINATION_ID) {
        badgeName = 'COVID vaccinated';
      }

      return {
        icon: BADGE_MAP[id],
        id,
        name: badgeName,
      };
    });

  if (repeatGuests > 0) {
    filteredBadgeData.push({
      icon: BADGE_MAP[40],
      id: 40,
      name: repeatGuests === 1 ? `${repeatGuests} Repeat guest` : `${repeatGuests} Repeat guests`,
    });
  }

  const lastCalendarUpdateDate = lastCalendarUpdate * 1000;
  if (Date.now() - lastCalendarUpdateDate <= CALENDAR_UPDATE_THRESHOLD) {
    filteredBadgeData.push({
      icon: BADGE_MAP[41],
      id: 41,
      name: 'Calendar recently updated',
    });
  }

  // TODO(WEB-2270): Revisit if we need this feature anymore
  // if (checkLastUpdateEnabled(city, state)) {
  //   filteredBadgeData.push({
  //     icon: BADGE_MAP[41],
  //     id: 41,
  //     name: `Calendar updated ${dayjs.unix(lastCalendarUpdate).fromNow()}`,
  //   });
  // }

  if (service) {
    const [selectedService] = SERVICE_TYPES.filter((serviceType) => serviceType.name === service);
    const sitterProvidesRwbForService = providesRwbPerService.includes(selectedService.id);

    if (sitterProvidesRwbForService) {
      filteredBadgeData.push({
        icon: BADGE_MAP[42],
        id: 42,
        name: 'Accepts weekly bookings',
      });
    }
  }

  return filteredBadgeData;
};

export const mapSitterCardDetails = (sitter: SearchData): SitterCardData => ({
  badges: transformBadgeData(
    sitter.badges.data,
    sitter.lastCalendarUpdate,
    sitter.providesRwbPerService,
    sitter.repeatGuests
  ),
  id: sitter.id,
  isSitterLiked: sitter.liked,
  location: {
    distanceFromSearchAddress: sitter.distance,
    suburbName: `${sitter.city}, ${sitter.state}`,
  },
  name: sitter.name,
  price: sitter.basePrice,
  profilePhoto: sitter.avatarV2,
  profileUrl: getSitterProfileUrl(sitter.city, sitter.rewriteUrl, sitter.state),
  rating: {
    numberOfReviews: sitter.totalReviews,
    score: sitter.rating,
  },
  tagline: sitter.headline,
});
